import React, { useState } from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import type { ContextReview } from '../../../types/smartStart';

interface ContextReviewBannerProps {
  review: ContextReview;
  onResolve: (choice: 'keep_project' | 'use_session') => Promise<void>;
}

const ContextReviewBanner: React.FC<ContextReviewBannerProps> = ({ review, onResolve }) => {
  const [submitting, setSubmitting] = useState<'keep_project' | 'use_session' | null>(null);
  const [resolved, setResolved] = useState<'keep_project' | 'use_session' | null>(null);

  const handle = async (choice: 'keep_project' | 'use_session') => {
    setSubmitting(choice);
    try {
      await onResolve(choice);
      setResolved(choice);
    } finally {
      setSubmitting(null);
    }
  };

  if (resolved) {
    return (
      <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2 text-sm text-emerald-800">
        <Check className="w-4 h-4 flex-shrink-0" />
        <span>
          {resolved === 'use_session'
            ? 'Analysis focus updated to this session’s context.'
            : 'Kept the project’s existing analysis focus.'}
        </span>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-900">Review analysis focus before generating</p>
          <p className="text-sm text-amber-800 mt-1">
            This project already has saved Analysis Focus Instructions that differ from this session.
            Confirm which context the AI should use so results aren’t skewed by a prior session.
          </p>

          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="rounded border border-amber-200 bg-white px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Saved on project</p>
              <p className="text-xs text-gray-700 mt-1 whitespace-pre-wrap break-words">
                {review.saved_instructions || '—'}
              </p>
            </div>
            <div className="rounded border border-amber-200 bg-white px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">This session</p>
              <p className="text-xs text-gray-700 mt-1 whitespace-pre-wrap break-words">
                {review.session_derived_instructions || '—'}
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={submitting !== null}
              onClick={() => handle('use_session')}
              className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {submitting === 'use_session' ? 'Applying…' : 'Use this session’s context'}
            </button>
            <button
              type="button"
              disabled={submitting !== null}
              onClick={() => handle('keep_project')}
              className="px-3 py-1.5 text-sm rounded-md border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 disabled:opacity-50"
            >
              {submitting === 'keep_project' ? 'Keeping…' : 'Keep project context'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContextReviewBanner;
